import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cls from 'classnames';

import container from 'containers/NetworkMembershipContainer';
import CourseCard from 'components/shared/ui/courses/CourseCard';

import { useDimension } from 'hooks/useDimensions';

const GolfClubCourseList = ({
  location,
  golfClubCourses,
  isFetchingGolfClubCoursesDone,
  setPreviousUrl,
  showBadges,
  wrapperStyles,
  handleCourseClick,
}) => {
  const { width } = useDimension();
  const courseContainerRef = useRef();
  const courseScrollRef = useRef();
  const history = useHistory();
  const isWebView = useSelector(({ app }) => app?.isWebView);

  const onCourseClick = (course) => {
    if (!course?.hierarchizedUrl) return;
    if (handleCourseClick) handleCourseClick(course);
    if (isWebView) {
      window.location.href = `${process.env.REACT_APP_WEBVIEW_REDIRECT_ORIGIN}/explore${course.hierarchizedUrl}`;
    } else {
      setPreviousUrl(location.pathname);
      history.push(`/explore${course.hierarchizedUrl}`);
    }
  };

  const handleCoursesScroll = (e, scrollParent) => {
    if (!courseContainerRef.current || !courseScrollRef.current) return;
    if (scrollParent) {
      courseContainerRef.current.scrollLeft = e.target.scrollLeft;
    } else {
      courseScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const renderCourseListSkeleton = Array(3).fill().map((_, index) => {
    const key = `course-skeleton-${index}`;
    return <CourseCard key={key} isSkeleton />;
  });

  return (
    <div className="overflow-hidden">
      <div
        ref={courseContainerRef}
        className={cls('mt-6 flex gap-4 overflow-x-auto pl-4 md:pl-10 lg:m-0 lg:p-0', {
          'no-scrollbar': width <= 1024,
          'scrollbar-thin lg:pb-4': width >= 1024,
        }, wrapperStyles)}
        onScroll={(e) => handleCoursesScroll(e, false)}
      >
        {isFetchingGolfClubCoursesDone ? golfClubCourses?.map((course) => (
          <CourseCard
            key={course?.id}
            course={course}
            styles={{ wrapper: 'last:mr-4 md:last:mr-10 lg:last:m-0' }}
            onClick={onCourseClick}
            show={showBadges}
          />
        )) : renderCourseListSkeleton}
      </div>
      {courseContainerRef.current && (
        <div ref={courseScrollRef} className="mx-4 scrollbar-thin overflow-x-auto md:mx-10 lg:hidden" onScroll={(e) => handleCoursesScroll(e, true)}>
          <div className="h-4" style={{ width: `${courseContainerRef.current.scrollWidth - (width >= 768 ? 80 : 32)}px` }} />
        </div>
      )}
    </div>
  );
};

GolfClubCourseList.defaultProps = {
  golfClubCourses: [],
  showBadges: false,
  wrapperStyles: '',
  handleCourseClick: null,
};

GolfClubCourseList.propTypes = {
  golfClubCourses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    photoMediumUrl: PropTypes.string,
    hierarchizedUrl: PropTypes.string,
    addressCity: PropTypes.string,
    addressState: PropTypes.string,
    minRate: PropTypes.number,
    maxRate: PropTypes.number,
    additionalFees: PropTypes.number,
    symbol: PropTypes.string,
    currency: PropTypes.string,
    showSymbol: PropTypes.bool,
    stats: PropTypes.shape({
      hasHotDeals: PropTypes.bool,
    }),
    hasNetworkMembership: PropTypes.bool,
    networkMembershipDiscountPercentage: PropTypes.number,
  })),
  isFetchingGolfClubCoursesDone: PropTypes.bool.isRequired,
  showBadges: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  setPreviousUrl: PropTypes.func.isRequired,
  wrapperStyles: PropTypes.string,
  handleCourseClick: PropTypes.func,
};

export default container(GolfClubCourseList);
